import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import './styles/NavBar.css';
import logo from './../assets/logo.webp';

import { useAuth } from "../context/AuthProvider";
import { useCart } from "../context/CartContext";
import { useStore } from "../context/StoreContext";



const NavBar = () => {
  const { auth, logout } = useAuth();
  const { cart } = useCart();
  const { categories } = useStore();

  const location = useLocation();


  useEffect(() => {
    // Clear any active focus on navigation
    document.activeElement.blur();
    handleDropdownClose(); // Optionally reset your dropdown state too
  }, [location]);



  const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);


  const [focusedMainItem, setFocusedMainItem] = useState(null); 


  const handleDropdownClose = () => {
    setFocusedMainItem(null);
};


  const handleMainFocus = (item) => setFocusedMainItem(item);
  const handleMainBlur = (e, item) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setFocusedMainItem(null);
      if (item === 'info') handleDropdownClose();
    }
  };

  return (
    <>

      <div className="navbar-wrapper" role="navigation" aria-label="Main navigation">
        <div className="navbar-content-container">

          <div className="navlinks-wrapper">
          
            <Link to='/' className='logo-link' aria-label="Home">
                <img src={logo} title='Go Back Home' alt='' width={"240px"}></img>
            </Link>
          
            <nav className="navlinks">
              <ul className="navlinks-list">
                <li 
                  className={`navlink-item ${focusedMainItem === 'home' ? 'focused' : ''}`}
                  onFocus={() => handleMainFocus('home')}
                  onBlur={(e) => handleMainBlur(e, 'home')}
                >
                  <NavLink to="/" className="navlink" activeclassname="active">
                    <span className="navlink-text">Home</span>
                  </NavLink>
                </li>
                <li
                  className={`navlink-item ${focusedMainItem === 'shop-all' ? 'focused' : ''}`}
                  onFocus={() => handleMainFocus('shop-all')}
                  onBlur={(e) => handleMainBlur(e, 'shop-all')}
                >
                  {categories.length > 1 ? (
                    <>
                      <NavLink to="/shop-knives/all-knives" className="navlink" aria-label="Shop Menu">
                        <span className="navlink-text">
                          Shop Knives <i className="fa-solid fa-angle-down"></i>
                        </span>
                      </NavLink>

                      <div className="user-dropdown-menu">
                        <NavLink to="/shop-knives/all-knives" className="dropdown-item">
                          All Products
                        </NavLink>
                        {categories.map((category) => (
                          <NavLink
                            key={category.id}
                            to={`/shop-knives/category/${category.name.toLowerCase().replace(/\s+/g, "-")}`}
                            className="dropdown-item"
                          >
                            {category.name}
                          </NavLink>
                        ))}
                      </div>
                    </>
                  ) : (
                    <NavLink to="/shop-knives/all-knives" className="navlink">
                      <span className="navlink-text">Shop Knives</span>
                    </NavLink>
                  )}
                </li>


                <li
                  className={`navlink-item ${focusedMainItem === 'info' ? 'focused' : ''}`}
                  onFocus={() => handleMainFocus('info')}
                  onBlur={(e) => handleMainBlur(e, 'info')}
                >
                  <NavLink to="/about" className="navlink" activeclassname="active" aria-label="Info Menu">
                    <span className="navlink-text">About <i className="fa-solid fa-angle-down"></i></span>
                  </NavLink>
                  <div className="user-dropdown-menu">
                    <NavLink to="/about" className="dropdown-item">
                      About Us
                    </NavLink>
                    <NavLink to="/our-process" className="dropdown-item">
                      Our Process
                    </NavLink>
                    <NavLink to="/shipping-returns" className="dropdown-item">
                      Shipping & Returns
                    </NavLink>
                    <NavLink to="/privacy-policy" className="dropdown-item">
                      Privacy Policy
                    </NavLink>
                  </div>
                </li>

                <li 
                  className={`navlink-item ${focusedMainItem === 'contact-us' ? 'focused' : ''}`}
                  onFocus={() => handleMainFocus('contact-us')}
                  onBlur={(e) => handleMainBlur(e, 'contact-us')}
                >
                  <NavLink to="/contact" className="navlink" activeclassname="active">
                    <span className="navlink-text">Contact Us</span>
                  </NavLink>
                </li>
              </ul>
            </nav>
          </div>
          <div className="navbar-icons-wrapper">



            {/* ✅ User Dropdown (Only when logged in) */}
            {auth.isAuthenticated ? (
              <div className="user-dropdown user">
                <div className="icon-link user-icon" aria-label="User Menu">
                <Link to="/user/profile" className="dropdown-item"><i className="fa-solid fa-user" tabIndex={-1}></i></Link>
                </div>
                <div className="user-dropdown-menu">
                  <Link to="/user/profile" className="dropdown-item"><i className="fa-solid fa-user"></i>&nbsp;My Profile</Link>
                  <Link to="/user/orders" className="dropdown-item"><i className="fa-solid fa-bag-shopping"></i>&nbsp;My Orders</Link>
                  {auth.isAuthenticated && ["admin", "siteadmin"].includes(auth.user?.role) && (

                  <Link to="/admin/dashboard" className="dropdown-item">
                    <i className="fa-solid fa-screwdriver-wrench"></i>&nbsp;Admin Dashboard
                  </Link>
                  
                  )}
                  <button onClick={logout} className="dropdown-item logout-button">
                    <i className="fa-solid fa-right-from-bracket"></i>&nbsp;Logout
                  </button>
                </div>
              </div>
            ) : (
              <div className="user-dropdown user">
                <button className="icon-link user-icon" aria-label="User Menu" tabIndex={-1}>
                  <Link to="/login" className="dropdown-item"><i className="fa-solid fa-user"></i><span className='sr-only'>Login</span></Link>
                </button>
                <div className="user-dropdown-menu">
                  <Link to="/login" className="dropdown-item">
                    <i className="fa-solid fa-right-to-bracket"></i>&nbsp;&nbsp;Login
                  </Link>
                  <Link to="/signup" className="dropdown-item">
                    <i className="fa-solid fa-user-plus"></i>&nbsp;&nbsp;Sign Up
                  </Link>
                </div>
              </div>
            )}


            <hr/>


            {/* Cart Icon (Unchanged) */}


            <div className="navbar-cart">
              <Link to="/shopping-cart" className="dropdown-item" aria-label="Cart">
                <i className="fa-solid fa-cart-shopping"></i>
                {cartItemCount > 0 && <span className="cart-count">{cartItemCount}</span>}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NavBar;